import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { AuthenticationMethod } from '@app/models';
import { PhoneService } from '@app/services/phone.service';

export type PhoneOtpProvider = 'whatsapp' | 'sms';

interface PhoneOtpProviderTranslations {
  authCodeSent: string;
  documentCodeSent: string;
  phoneEntry: string;
  notification: string;
}

type PhoneOtpProviderTranslation = keyof PhoneOtpProviderTranslations;

interface PhoneOtpProviderTranslationOptions {
  instant?: boolean;
  params?: Record<string, unknown>;
}

interface PhoneOtpProviderConfig {
  authenticationMethod: AuthenticationMethod;
  securityCodeType: PhoneOtpProvider;
  translations: PhoneOtpProviderTranslations;
  label: string;
}

const PHONE_OTP_PROVIDER_CONFIGS: Record<PhoneOtpProvider, PhoneOtpProviderConfig> = {
  sms: {
    authenticationMethod: AuthenticationMethod.Sms,
    securityCodeType: 'sms',
    translations: {
      authCodeSent: 'auth.codeSendSms',
      documentCodeSent: 'documents.smsSent',
      phoneEntry: 'shared.enterPhoneNumberSMS',
      notification: 'notyService.smsVerificationSent'
    },
    label: 'SMS'
  },
  whatsapp: {
    authenticationMethod: AuthenticationMethod.Whatsapp,
    securityCodeType: 'whatsapp',
    translations: {
      authCodeSent: 'auth.codeSendWhatsapp',
      documentCodeSent: 'documents.whatsAppSent',
      phoneEntry: 'shared.enterPhoneNumberWhatsapp',
      notification: 'notyService.whatsappVerificationSent'
    },
    label: 'WhatsApp'
  }
};

const SMS_DEFAULT_PHONE_DIAL_CODES = [
  '+1', // Estados Unidos (US)
  '+1', // Canada (CA)
  '+61', // Australia (AU)
  '+64', // Nova Zelandia (NZ)
  '+45', // Dinamarca (DK)
  '+47', // Noruega (NO)
  '+46', // Suecia (SE)
  '+358', // Finlandia (FI)
  '+81', // Japao (JP)
  '+82', // Coreia do Sul (KR)
  '+86', // China (CN)
  '+84', // Vietna (VN)
  '+63', // Filipinas (PH)
  '+66', // Tailandia (TH)
  '+886', // Taiwan (TW)
  '+7', // Russia (RU)
  '+98', // Ira (IR)
  '+850' // Coreia do Norte (KP)
];

@Injectable({ providedIn: 'root' })
export class PhoneOtpProviderService {
  private readonly enabledProviders: PhoneOtpProvider[] = ['whatsapp', 'sms']; // Taking them off hides them. The first one is used by default.

  constructor(
    private phoneService: PhoneService,
    private translateService: TranslateService
  ) {}

  get primaryProvider(): PhoneOtpProvider {
    return this.enabledProviders[0];
  }

  primaryProviderForPhone(phoneNumber?: string): PhoneOtpProvider {
    if (this.isSmsDefaultPhoneNumber(phoneNumber)) {
      return this.normalizeProvider('sms');
    }

    return this.primaryProvider;
  }

  normalizeProvider(provider?: PhoneOtpProvider): PhoneOtpProvider {
    const requestedProvider = provider || this.primaryProvider;

    if (this.enabledProviders.includes(requestedProvider)) {
      return requestedProvider;
    }

    return this.primaryProvider;
  }

  normalizeProviderForPhone(phoneNumber?: string, provider?: PhoneOtpProvider): PhoneOtpProvider {
    if (provider) {
      return this.normalizeProvider(provider);
    }

    return this.primaryProviderForPhone(phoneNumber);
  }

  providerFromAuthenticationMethod(method?: AuthenticationMethod): PhoneOtpProvider {
    if (method === AuthenticationMethod.Whatsapp) {
      return this.normalizeProvider('whatsapp');
    }
    if (method === AuthenticationMethod.Sms) {
      return this.normalizeProvider('sms');
    }

    return this.primaryProvider;
  }

  isPhoneAuthentication(method: AuthenticationMethod): boolean {
    return [AuthenticationMethod.Sms, AuthenticationMethod.Whatsapp].includes(method);
  }

  fallbackProvider(provider: PhoneOtpProvider = this.primaryProvider): PhoneOtpProvider | null {
    const currentProvider = this.normalizeProvider(provider);
    return this.enabledProviders.find(enabledProvider => enabledProvider !== currentProvider) || null;
  }

  hasFallbackProvider(provider: PhoneOtpProvider = this.primaryProvider): boolean {
    return !!this.fallbackProvider(provider);
  }

  authenticationMethod(provider: PhoneOtpProvider = this.primaryProvider): AuthenticationMethod {
    return this.providerConfig(provider).authenticationMethod;
  }

  normalizeAuthenticationMethod(method: AuthenticationMethod): AuthenticationMethod {
    if (this.isPhoneAuthentication(method)) {
      return this.authenticationMethod(this.providerFromAuthenticationMethod(method));
    }

    return method;
  }

  securityCodeType(provider: PhoneOtpProvider = this.primaryProvider): PhoneOtpProvider {
    return this.providerConfig(provider).securityCodeType;
  }

  translation(key: PhoneOtpProviderTranslation, provider: PhoneOtpProvider = this.primaryProvider, options: PhoneOtpProviderTranslationOptions = {}): string {
    const translationKey = this.providerConfig(provider).translations[key];
    return options.instant ? this.translateService.instant(translationKey, options.params) : translationKey;
  }

  providerLabel(provider: PhoneOtpProvider = this.primaryProvider): string {
    return this.providerConfig(provider).label;
  }

  sendVerification(phoneNumber: string, forceUserCreation?: boolean, provider: PhoneOtpProvider = this.primaryProvider) {
    return this.normalizeProvider(provider) === 'whatsapp'
      ? this.phoneService.sendWhatsappVerification(phoneNumber, forceUserCreation)
      : this.phoneService.sendSmsVerification(phoneNumber, forceUserCreation);
  }

  submitVerificationCode(phoneNumber: string, verificationCode: string, provider: PhoneOtpProvider = this.primaryProvider) {
    return this.normalizeProvider(provider) === 'whatsapp'
      ? this.phoneService.submitWhatsappVerificationCode(phoneNumber, verificationCode)
      : this.phoneService.submitSmsVerificationCode(phoneNumber, verificationCode);
  }

  private providerConfig(provider: PhoneOtpProvider): PhoneOtpProviderConfig {
    return PHONE_OTP_PROVIDER_CONFIGS[this.normalizeProvider(provider)];
  }

  private isSmsDefaultPhoneNumber(phoneNumber?: string): boolean {
    const sanitizedPhoneNumber = PhoneService.sanitizePhoneInput(phoneNumber || '');
    if (!sanitizedPhoneNumber) {
      return false;
    }

    const internationalPhoneNumber = sanitizedPhoneNumber.startsWith('+') ? sanitizedPhoneNumber : `+${sanitizedPhoneNumber}`;
    return SMS_DEFAULT_PHONE_DIAL_CODES.some(dialCode => internationalPhoneNumber.startsWith(dialCode));
  }
}
